import theme from "theme";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import { Box, Card, IconButton, Popover, Tooltip } from "@mui/material";
import { CustomCaseProperty, FormField } from "generated/graphql";
import { usePopover } from "helpers/hooks/usePopover";
import { Fragment, useContext, useEffect, useState } from "react";
import { CaseFormContext } from "contexts/CaseFormContext";
import FormFieldProvider from "routes/lenses/lens/case/FormFieldProvider";
import { useAppSelector } from "redux/hooks";
import { selectForm } from "redux/slices/form.slice";
import { EntryTable } from "./FormField/EntryTable";
import { FormFieldName } from "./FormField/FormFieldName";
import { FormFieldValueGroupUI, RowViewState } from "Backend";
import { ArrowDropDown, ArrowDropUp, OpenInNew } from "@mui/icons-material";
import { P } from "theme/typography";
import { PCopy } from "./PCopy";
import { FormFieldContext } from "contexts/FormFieldContext";
import { useShowSBR } from "SBR/useShowSBR";
import { cn } from "./lib/utils";
import { EntryContext } from "./tags/types";
import { ContextDialog } from "./ContextDialog/ContextDialog";
import { ContextMode } from "./ContextDialog/types";

function LinkedField({ formField, valueGroups }: { formField: FormField; valueGroups: FormFieldValueGroupUI[] }) {
  const [open, setOpen] = useState(true);
  const ffContext = useContext(FormFieldContext);
  const [entryContext, setEntryContext] = useState<EntryContext>();

  // useEffect necessary here to make sure we only set the view state to collapsed on first render
  // without it, clicking the row to expand will not work since the forEach loop would reset it back to collapsed
  useEffect(() => {
    valueGroups.forEach((vg) => {
      ffContext.setValueGroupViewState(vg.id, RowViewState.ReadCollapsed);
    });
  }, []);

  return (
    <>
      <div className="flex items-center justify-between min-w-80">
        <FormFieldName formField={formField} compactMode />
        <IconButton size="small" onClick={() => setOpen((prev) => !prev)}>
          {open ? <ArrowDropDown /> : <ArrowDropUp />}
        </IconButton>
      </div>
      <EntryTable
        readonly
        valueGroups={valueGroups}
        hideCellSelector
        compactMode
        tableLayout="auto"
        onReferenceClick={setEntryContext}
        expanded={open}
        setExpanded={setOpen}
        showExpander={false}
      />
      {entryContext && (
        <ContextDialog
          open={true}
          mode={ContextMode.EntryViewer}
          onClose={() => setEntryContext(undefined)}
          componentProps={{
            entryContext,
            setEntryContext,
            onClose: () => setEntryContext(undefined),
            isLinkable: false,
          }}
        />
      )}
    </>
  );
}

export function FullCalcCard({
  title,
  description,
  value,
  explanation,
  linkedValueGroups,
}: {
  title: string;
  value: number | string;
  description?: string;
  explanation?: string;
  linkedValueGroups?: number[];
}) {
  const form = useAppSelector(selectForm);
  const cfContext = useContext(CaseFormContext);
  const linkedFieldsAndValueGroups = cfContext.fieldsForValueGroups(linkedValueGroups ?? []);
  const showLinkedResponses = form !== undefined && linkedFieldsAndValueGroups.length > 0;
  return (
    <Card className="w-[800px]">
      <div className="p-4 flex flex-col gap-2">
        <div className="grid grid-cols-3 gap-4 py-2">
          <div className="bg-gray-50 rounded-lg">
            <div className="flex flex-col gap-2 h-full p-3 w-full">
              <Tooltip placement="top" title={title}>
                <p className="text-xxs line-clamp-2">{title}</p>
              </Tooltip>
              <PCopy className="w-full text-sm line-clamp-2" text={value.toString()}>
                {value}
              </PCopy>
            </div>
          </div>
          {description !== undefined && (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold">Description</p>
              {description !== undefined && <p className="text-xs">{description}</p>}
            </div>
          )}
          {explanation !== undefined && (
            <div className="flex flex-col gap-1">
              <div className="flex gap-2 items-center">
                <p className="text-sm font-bold">Explanation</p>
              </div>
              <p className="text-xs">{explanation}</p>
            </div>
          )}
        </div>
        {showLinkedResponses && (
          <div className="border border-gray-100 p-2 mt-2 flex flex-col gap-2">
            <p className="font-bold">Linked Entries</p>
            {linkedFieldsAndValueGroups.map((fieldAndValueGroups) => (
              <Fragment key={fieldAndValueGroups.formField.id}>
                <FormFieldProvider formField={fieldAndValueGroups.formField} form={form}>
                  <LinkedField
                    formField={fieldAndValueGroups.formField}
                    valueGroups={fieldAndValueGroups.valueGroups}
                  />
                </FormFieldProvider>
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export interface MetricProps {
  title: string;
  description?: string;
  value: number | string;
  updated?: string;
  rowIndex?: number | null;
  colIndex?: number | null;
  caseProperty?: CustomCaseProperty;
  explanation?: string;
  linkedValueGroups?: number[];
}

function Metric({ metric }: { metric: MetricProps }) {
  const { title, updated, value, explanation, linkedValueGroups } = metric;
  const popover = usePopover();
  const canShowFullCalcCard = explanation !== undefined || linkedValueGroups !== undefined;

  return (
    <>
      <div className="bg-gray-50 rounded-lg flex gap-1">
        <div className="flex flex-col h-full p-3 w-full gap-2">
          <div className="flex gap-1">
            <Tooltip
              placement="top"
              title={
                <div className="flex flex-col gap-1">
                  <p className="text-xs">{title}</p>
                  {updated !== undefined && <p className="text-xxs">Updated at {updated}</p>}
                </div>
              }>
              <p className="text-xxs line-clamp-2">{title}</p>
            </Tooltip>
            {!canShowFullCalcCard && (
              <DescriptionTooltip sx={{ height: 14, width: 14 }} description={metric.description} />
            )}
          </div>
          <PCopy className="w-full text-sm line-clamp-2" text={value.toString()}>
            {value}
          </PCopy>
        </div>
        {canShowFullCalcCard && (
          <div>
            <Tooltip title="Details">
              <IconButton className="text-gray-200" onClick={(evt) => popover.onPopoverOpen(evt.currentTarget)}>
                <OpenInNew />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>
      <Popover
        open={popover.isOpen}
        anchorEl={popover.anchorEl}
        onClose={popover.onPopoverClose}
        sx={{ maxWidth: 1200 }}>
        <FullCalcCard
          title={title}
          explanation={explanation}
          linkedValueGroups={linkedValueGroups}
          description={metric.description}
          value={value}
        />
      </Popover>
    </>
  );
}

interface MetricsToolbarProps {
  title?: string;
  metrics: MetricProps[];
}

const MetricsToolbar = ({ title, metrics }: MetricsToolbarProps) => {
  const showSBR = useShowSBR();

  return (
    <div className="h-full p-4 flex flex-col">
      {title && <p className="mb-2 font-semibold ">{title}</p>}
      <div className={cn("grid gap-4", showSBR ? "grid-cols-4" : "grid-cols-5")}>
        {metrics.map((metric, index) => (
          <Metric key={index} metric={metric} />
        ))}
      </div>
    </div>
  );
};

export default MetricsToolbar;

export const MetricsTransparentBar = ({ metrics }: { metrics: MetricProps[] }) => {
  return (
    <Box
      sx={{
        height: "100%",
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        borderTop: `1px solid ${theme.palette.grey[100]}`,
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
      }}>
      {metrics.map((m, index) => (
        <Box
          sx={{
            flexGrow: 1,
            borderRight: index < metrics.length - 1 ? `1px solid ${theme.palette.grey[100]}` : "",
            pl: 2,
          }}
          key={index}>
          <P variant="subtitle1" sx={{ color: "grey.300", mb: 1 }}>
            {m.title}
          </P>
          <P variant="h3">{m.value}</P>
        </Box>
      ))}
    </Box>
  );
};
